import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { EVENTS } from '~/config/events';
import { MODALS } from '~/config/modals';

import { useServiceContainer, useStoreContainer } from '~/container';

import DebugBar from '~/modules/core/components/DebugBar.vue';
import EtAgnosticRouterLink from '~/modules/core/components/EtAgnosticRouterLink.vue';
import EtCookieBanner from '~/modules/core/components/EtCookieBanner.vue';
import EtCurrencySelectorButton from '~/modules/core/components/EtCurrencySelectorButton.vue';
import EtFlag from '~/modules/core/components/EtFlag.vue';
import EtFooterAccordion from '~/modules/core/components/EtFooterAccordion.vue';
import EtLocaleSelector from '~/modules/core/components/EtLocaleSelector.vue';
import EtMenuButton from '~/modules/core/components/EtMenuButton.vue';
import EtNewsletterSubscribeModalButton from '~/modules/core/components/EtNewsletterSubscribeModalButton.vue';
import EtStateResolve from '~/modules/core/components/EtStateResolve.vue';
import EtModalContainer from '~/modules/core/components/modal/EtModalContainer.vue';
import EtOverlayContainer from '~/modules/core/components/overlay/EtOverlayContainer.vue';
import EtTooltipContainer from '~/modules/core/components/tooltip/EtTooltipContainer.vue';
import EtMainSearchOverlayBtn from '~/modules/searchQuery/components/EtMainSearchOverlayBtn.vue';
import EtForgotPasswordLink from '~/modules/user/components/EtForgotPasswordLink.vue';
import EtUserLoginButton from '~/modules/user/components/EtUserLoginButton.vue';
import EtUserLogoutButton from '~/modules/user/components/EtUserLogoutButton.vue';
import EtUserMenu from '~/modules/user/components/EtUserMenu.vue';
import EtUserMenuButton from '~/modules/user/components/EtUserMenuButton.vue';
import EtUserRegisterButton from '~/modules/user/components/EtUserRegisterButton.vue';

import { LegacyAccountRouteNames } from '~/router/names/account';
import { LegacyCoreRouteNames } from '~/router/names/core';
import { LegacySearchBookingRouteNames } from '~/router/names/search-booking';

export default defineComponent({
    // Add all components which are loaded outside of pages.
    // For example components loaded in server side twig files.
    components: {
        DebugBar,
        EtCookieBanner,
        EtStateResolve,
        EtModalContainer,
        EtOverlayContainer,
        EtTooltipContainer,
        EtUserLoginButton,
        EtCurrencySelectorButton,
        EtLocaleSelector,
        EtUserMenuButton,
        EtUserMenu,
        EtUserRegisterButton,
        EtForgotPasswordLink,
        EtNewsletterSubscribeModalButton,
        EtUserLogoutButton,
        EtMainSearchOverlayBtn,
        EtMenuButton,
        EtFooterAccordion,
        EtFlag,
        EtAgnosticRouterLink,
    },
    setup() {
        const {
            EventBusService,
            BodyClassService,
            DataLayerService,
            VisitService,
            BackendLoggerService,
            LoggerService,
            ModalService,
        } = useServiceContainer();
        const { AppStore, AppStoreRefs, BodyClassStore, UserStoreRefs, ConfigStore, VisitStoreRefs } =
            useStoreContainer();
        const { loadingState } = AppStoreRefs;

        const isDev = import.meta.env.DEV && import.meta.env.VITE_DEBUG_BAR_ENABLED === 'true';
        const route = useRoute();
        const router = useRouter();
        const { isAuthenticated: userIsAuthenticated } = UserStoreRefs;
        const { frontendConfiguration } = ConfigStore;
        const { currentVisit } = VisitStoreRefs;

        const visitExpirationTimer = ref<null | ReturnType<typeof setTimeout>>(null);
        const visitLoaded = ref(false);

        // Check visit on window focus (timeouts/intervals don't run in background windows in some browsers)
        EventBusService.on(EVENTS.windowFocused, () => checkVisitExpiration());

        // We need to set route to the store because useRoute cannot be used outside of setup,
        // thus it cannot be used in services. Therefor we store it.

        watch(
            () => BodyClassStore.getCssClasses,
            (value) => BodyClassService.setClassList(value),
            { deep: true, immediate: true },
        );

        watch(
            () => route,
            () => handleUnauthenticatedRoute(),
            { deep: true, immediate: true },
        );

        watch(
            () => userIsAuthenticated.value,
            () => handleUnauthenticatedRoute(),
        );

        watch(
            () => currentVisit.value,
            () => {
                if (!currentVisit.value) {
                    return;
                }

                visitLoaded.value = true;
            },
            { immediate: true },
        );

        watch(
            () => visitLoaded.value,
            () => {
                if (!visitLoaded.value) {
                    return;
                }

                watchVisitExpiration();
                addMultiVariateModalEventListeners();
            },
            { immediate: true },
        );

        watch(
            () => route.name,
            () => {
                // Hide header navigation in checkout
                const hideHeaderNav =
                    typeof route.name === 'string' && route.name.startsWith(LegacySearchBookingRouteNames.BookingPage);

                BodyClassService.toggle('et-body--hide-header-nav', hideHeaderNav);
            },
            { immediate: true },
        );

        onMounted(() => {
            applyDatatricsConfig();
        });

        onBeforeUnmount(() => {
            EventBusService.clearAll();

            removeMultiVariateModalEventListener();
        });

        function handleUnauthenticatedRoute() {
            if (
                route?.name &&
                [LegacyCoreRouteNames.LoginPage, LegacyCoreRouteNames.RegisterPage].includes(
                    route.name as LegacyCoreRouteNames,
                ) &&
                userIsAuthenticated.value
            ) {
                return router.push({ name: LegacyAccountRouteNames.BookingHistoryPage });
            }
        }

        function addMultiVariateModalEventListeners() {
            document.addEventListener('keydown', openMultiVariateModal);
        }

        function removeMultiVariateModalEventListener() {
            document.removeEventListener('keydown', openMultiVariateModal);
        }

        function openMultiVariateModal(event: KeyboardEvent) {
            // ALT + CTRL + M
            if (event.ctrlKey && event.altKey && event.code === 'KeyM') {
                event.preventDefault();

                return ModalService.open({
                    id: MODALS.etMultiVariateSelectorModal,
                    size: 'lg',
                });
            }
        }
        function watchVisitExpiration() {
            if (visitExpirationTimer.value) {
                clearTimeout(visitExpirationTimer.value);
            }

            if (currentVisit.value) {
                visitExpirationTimer.value = setTimeout(
                    () => checkVisitExpiration(),
                    VisitService.createVisitExpirationDate(currentVisit.value).getTime() - new Date().getTime(),
                );
            }
        }

        function checkVisitExpiration() {
            // skip this check if the visit has not been loaded (yet/successfully)
            if (currentVisit.value && VisitService.isExpired(currentVisit.value) && !AppStore.hasSessionExpired()) {
                BackendLoggerService.log('info', 'Visit expired in etApp', {
                    isExpired: VisitService.isExpired(currentVisit.value),
                    hasSessionExpired: AppStore.hasSessionExpired(),
                    config: ConfigStore.getConfig(),
                    visit: currentVisit.value,
                });
                LoggerService.dump('EtAppComponent.checkVisitExpiration', [currentVisit.value]);
                AppStore.setSessionExpired(true);

                return ModalService.open({
                    id: MODALS.etVisitExpiredModal,
                    static: 'full',
                });
            }
        }

        function applyDatatricsConfig() {
            const isDatatricsEnabled = ConfigStore.isFeatureSwitchEnabled('datatrics');

            if (isDatatricsEnabled) {
                DataLayerService.pushEvent('DatatricsSwitch', {
                    datatrics_enabled: isDatatricsEnabled,
                });
            }
        }

        return {
            loadingState,
            isDev,
            route,
            userIsAuthenticated,
            config: frontendConfiguration,
            visit: currentVisit,
        };
    },
});
